import React,{useState} from 'react'
import {motion} from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import * as localStorageService from '../lib/localStorageService'

const {FiUser,FiLock,FiEye,FiEyeOff,FiLogIn}=FiIcons

const Login=({onLogin})=> {
  const [formData, setFormData] = useState({email: '', password: ''})
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isRegisterMode, setIsRegisterMode] = useState(false)

  const handleChange = (e)=> {
    const {name, value} = e.target
    setFormData(prev=> ({...prev, [name]: value}))
    if (error) setError('')
  }

  const handleSubmit = async (e)=> {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    
    try {
      if (isRegisterMode) {
        // Registration
        const {data, error} = await localStorageService.createUser({
          email: formData.email,
          password: formData.password,
          name: formData.email.split('@')[0] // Use email prefix as name
        })
        
        if (error) {
          throw new Error(error.message)
        }
        
        if (data.user) {
          // Auto login after registration
          const {data: loginData, error: loginError} = await localStorageService.signIn(formData.email, formData.password)
          if (loginError) {
            throw new Error(loginError.message)
          }
          if (loginData.user) {
            await onLogin(loginData.user)
          }
        }
      } else {
        // Login
        const {data, error} = await localStorageService.signIn(formData.email, formData.password)
        if (error) {
          throw new Error(error.message)
        }
        if (data.user) {
          await onLogin(data.user)
        }
      }
    } catch (error) {
      console.error('❌ Auth error:', error)
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-[#6e725c] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUser} className="text-2xl text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#4d504a] mb-2">Opomnik računov</h1>
          <p className="text-[#6e725c] text-sm">
            {isRegisterMode ? 'Ustvarite nov račun' : 'Prijavite se za dostop do aplikacije'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiUser} className="inline mr-2" />
              E-pošta
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all"
              placeholder="vnesite@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4d504a] mb-2">
              <SafeIcon icon={FiLock} className="inline mr-2" />
              Geslo
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6e725c] focus:border-transparent transition-all"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={()=> setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6e725c] hover:text-[#4d504a] transition-colors"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-lg" />
              </button>
            </div>
            {isRegisterMode && (
              <p className="text-xs text-[#6e725c] mt-1">Najmanj 6 znakov</p>
            )}
          </div>

          <motion.button
            type="submit"
            disabled={isLoading}
            whileHover={{scale: isLoading ? 1 : 1.02}}
            whileTap={{scale: isLoading ? 1 : 0.98}}
            className="w-full bg-[#6e725c] text-white py-3 rounded-lg font-semibold hover:bg-[#5d6250] transition-colors shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <SafeIcon icon={FiLogIn} className="mr-2" />
                {isRegisterMode ? 'Registracija' : 'Prijava'}
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={()=> {
              setIsRegisterMode(!isRegisterMode)
              setError('')
            }}
            className="text-[#6e725c] hover:text-[#4d504a] text-sm transition-colors"
          >
            {isRegisterMode 
              ? 'Že imate račun? Prijavite se' 
              : 'Še nimate računa? Registrirajte se'
            }
          </button>
        </div>

        {error && (
          <motion.div
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
          >
            {error}
          </motion.div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700 text-center">
            <strong>Lokalni način:</strong> Podatki so shranjeni v vašem brskalniku.
            Izvozite jih za varnostno kopiranje.
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default Login